import { useState, useMemo, useEffect } from "react";
import { Link } from "wouter";
import { SmartLayout } from "@/components/layout";
import { SEOHead } from "@/components/seo-head";
import { useListPublicTrainers, useGetStudentGyms } from "@workspace/api-client-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { Search, MapPin, Star, Users, ArrowRight, Building2, X, Sparkles, DollarSign, ArrowUpDown, TrendingUp, Award, ChevronDown, ChevronUp } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { cn } from "@/lib/utils";

const RATING_OPTIONS = [
  { label: "4.0+", value: 4.0 },
  { label: "4.5+", value: 4.5 },
  { label: "4.8+", value: 4.8 },
];

const PRICE_OPTIONS = [
  { label: "Under $50", value: 50 },
  { label: "Under $75", value: 75 },
  { label: "Under $100", value: 100 },
  { label: "Under $150", value: 150 },
];

type SortOption = "best_rated" | "most_sessions" | "price_asc" | "price_desc";

const SORT_OPTIONS: { value: SortOption; label: string; icon: React.ReactNode }[] = [
  { value: "best_rated",    label: "Best Rated",         icon: <Award className="w-3 h-3" /> },
  { value: "most_sessions", label: "Most Sessions",      icon: <TrendingUp className="w-3 h-3" /> },
  { value: "price_asc",     label: "Price: Low → High",  icon: <ChevronUp className="w-3 h-3" /> },
  { value: "price_desc",    label: "Price: High → Low",  icon: <ChevronDown className="w-3 h-3" /> },
];

export default function ExploreTrainers() {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const isStudent = isAuthenticated && user?.role === "STUDENT";

  const { data: studentGyms, isLoading: gymsLoading } = useGetStudentGyms({
    query: { enabled: isStudent },
  });

  const primaryGym = useMemo(() => {
    if (!studentGyms || studentGyms.length === 0) return null;
    return studentGyms.find((sg) => sg.isPrimary) || studentGyms[0];
  }, [studentGyms]);

  const [search, setSearch] = useState("");
  const [activeSpecialties, setActiveSpecialties] = useState<Set<string>>(new Set());
  const [myGymOnly, setMyGymOnly] = useState(false);
  const [minRating, setMinRating] = useState<number | null>(null);
  const [maxPrice, setMaxPrice] = useState<number | null>(null);
  const [sortBy, setSortBy] = useState<SortOption>("best_rated");
  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    if (hasInitialized) return;
    if (authLoading) return;
    if (!isStudent) { setHasInitialized(true); return; }
    if (gymsLoading) return;
    setHasInitialized(true);
    if (primaryGym) setMyGymOnly(true);
  }, [authLoading, isStudent, gymsLoading, primaryGym, hasInitialized]);

  const { data: trainers, isLoading } = useListPublicTrainers();

  const allSpecialties = useMemo(() => {
    if (!trainers) return [];
    const freq: Record<string, number> = {};
    for (const t of trainers) {
      for (const s of t.specialties ?? []) freq[s] = (freq[s] ?? 0) + 1;
    }
    return Object.entries(freq).sort((a, b) => b[1] - a[1]).map(([s]) => s);
  }, [trainers]);

  const homeGymSpecialties = useMemo(() => {
    if (!trainers || !primaryGym) return new Set<string>();
    const gymId = primaryGym.gymId;
    const set = new Set<string>();
    for (const t of trainers) {
      if (t.gymId === gymId) for (const s of t.specialties ?? []) set.add(s);
    }
    return set;
  }, [trainers, primaryGym]);

  const orderedSpecialties = useMemo(() => {
    const home = allSpecialties.filter((s) => homeGymSpecialties.has(s));
    const rest = allSpecialties.filter((s) => !homeGymSpecialties.has(s));
    return [...home, ...rest];
  }, [allSpecialties, homeGymSpecialties]);

  function toggleSpecialty(s: string) {
    setActiveSpecialties((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s); else next.add(s);
      return next;
    });
  }

  function clearFilters() {
    setSearch("");
    setActiveSpecialties(new Set());
    setMyGymOnly(false);
    setMinRating(null);
    setMaxPrice(null);
    setSortBy("best_rated");
  }

  const hasFilters = search.trim() !== "" || activeSpecialties.size > 0 || myGymOnly || minRating !== null || maxPrice !== null || sortBy !== "best_rated";

  const filtered = useMemo(() => {
    if (!trainers) return [];
    let result = trainers;

    if (myGymOnly && primaryGym) {
      result = result.filter((t) => t.gymId === primaryGym.gymId);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (t) =>
          t.firstName.toLowerCase().includes(q) ||
          t.lastName.toLowerCase().includes(q) ||
          (t.gymName && t.gymName.toLowerCase().includes(q)) ||
          (t.specialties && t.specialties.some((s) => s.toLowerCase().includes(q)))
      );
    }
    if (activeSpecialties.size > 0) {
      result = result.filter(
        (t) => t.specialties && t.specialties.some((s) => activeSpecialties.has(s))
      );
    }
    if (minRating !== null) {
      result = result.filter((t) => t.avgRating != null && Number(t.avgRating) >= minRating);
    }
    if (maxPrice !== null) {
      result = result.filter((t) => t.hourlyRate != null && Number(t.hourlyRate) < maxPrice);
    }

    // Sort
    result = [...result].sort((a, b) => {
      switch (sortBy) {
        case "best_rated": {
          const ra = a.avgRating != null ? Number(a.avgRating) : -1;
          const rb = b.avgRating != null ? Number(b.avgRating) : -1;
          return rb - ra;
        }
        case "most_sessions":
          return (b.totalSessions ?? 0) - (a.totalSessions ?? 0);
        case "price_asc": {
          const pa = a.hourlyRate != null ? Number(a.hourlyRate) : Infinity;
          const pb = b.hourlyRate != null ? Number(b.hourlyRate) : Infinity;
          return pa - pb;
        }
        case "price_desc": {
          const pa = a.hourlyRate != null ? Number(a.hourlyRate) : -Infinity;
          const pb = b.hourlyRate != null ? Number(b.hourlyRate) : -Infinity;
          return pb - pa;
        }
        default:
          return 0;
      }
    });

    return result;
  }, [trainers, search, activeSpecialties, myGymOnly, primaryGym, minRating, maxPrice, sortBy]);

  const studentStillLoading = isStudent && (gymsLoading || !hasInitialized);
  const showNoGymPrompt = isStudent && !gymsLoading && !authLoading && studentGyms && studentGyms.length === 0;

  if (showNoGymPrompt) {
    return (
      <SmartLayout>
        <SEOHead
          title="Find Martial Arts Trainers — Book Private Lessons"
          description="Discover top martial arts trainers across all gyms on Open Mat. View ratings, specialties, and hourly rates. Book a private lesson with any trainer."
          canonicalPath="/explore/trainers"
        />
        <section className="py-16 px-6 lg:px-12">
          <div className="max-w-md mx-auto text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mx-auto mb-6">
                <Building2 className="w-8 h-8 text-primary" />
              </div>
              <h1 className="text-3xl font-display font-bold text-white mb-3">Set Your Home Gym First</h1>
              <p className="text-white/50 mb-8">
                To browse trainers, you need to add a home gym to your profile. This helps us show you the most relevant trainers near you.
              </p>
              <Link href="/profile">
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold rounded-xl gap-2">
                  <Building2 className="w-4 h-4" /> Go to Profile to Add a Gym
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>
      </SmartLayout>
    );
  }

  return (
    <SmartLayout>
      <SEOHead
        title="Find Martial Arts Trainers — Book Private Lessons"
        description="Discover top martial arts trainers across all gyms on Open Mat. View ratings, specialties, and hourly rates. Book a private lesson with any trainer."
        canonicalPath="/explore/trainers"
      />

      <section className="py-16 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
            <h1 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">Find Trainers</h1>
            <p className="text-white/50 text-lg max-w-xl mx-auto">
              Discover top trainers across all gyms. Book a private lesson with anyone.
            </p>
          </motion.div>

          {/* Filter panel */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="max-w-3xl mx-auto mb-10 space-y-3"
          >
            {/* Search bar */}
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
              <Input
                placeholder="Search by name, gym, or specialty..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-12 h-12 bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/30 rounded-xl focus:border-primary/50"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {!studentStillLoading && (
              <div className="rounded-2xl bg-white/[0.02] border border-white/[0.06] p-4 space-y-3">

                {/* Row 1: Gym + specialties */}
                <div className="flex flex-wrap gap-2 items-center">
                  <span className="text-[11px] font-semibold text-white/25 uppercase tracking-widest w-16 shrink-0">
                    Specialty
                  </span>
                  <div className="flex flex-wrap gap-2 flex-1">
                    {isStudent && primaryGym && (
                      <button
                        onClick={() => setMyGymOnly((v) => !v)}
                        className={cn(
                          "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all",
                          myGymOnly
                            ? "bg-primary text-primary-foreground border-primary shadow-lg shadow-primary/20"
                            : "bg-white/[0.04] text-white/60 border-white/[0.10] hover:bg-white/[0.08] hover:text-white"
                        )}
                      >
                        <Building2 className="w-3 h-3" />
                        {primaryGym.gym?.name ?? "My Gym"}
                      </button>
                    )}
                    {orderedSpecialties.map((s) => {
                      const isActive = activeSpecialties.has(s);
                      const isSuggested = homeGymSpecialties.has(s);
                      return (
                        <button
                          key={s}
                          onClick={() => toggleSpecialty(s)}
                          className={cn(
                            "flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium border transition-all",
                            isActive
                              ? "bg-primary text-primary-foreground border-primary shadow-lg shadow-primary/20"
                              : isSuggested
                              ? "bg-primary/10 text-primary border-primary/30 hover:bg-primary/20"
                              : "bg-white/[0.04] text-white/50 border-white/[0.08] hover:bg-white/[0.08] hover:text-white/80"
                          )}
                        >
                          {isSuggested && !isActive && <Sparkles className="w-2.5 h-2.5" />}
                          {s}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="border-t border-white/[0.06]" />

                {/* Row 2: Rating + Price */}
                <div className="flex flex-wrap gap-4">

                  {/* Rating */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] font-semibold text-white/25 uppercase tracking-widest w-16 shrink-0">
                      Rating
                    </span>
                    {RATING_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => setMinRating((v) => v === opt.value ? null : opt.value)}
                        className={cn(
                          "flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium border transition-all",
                          minRating === opt.value
                            ? "bg-amber-500/20 text-amber-400 border-amber-500/40 shadow-lg shadow-amber-500/10"
                            : "bg-white/[0.04] text-white/50 border-white/[0.08] hover:bg-white/[0.07] hover:text-white/80"
                        )}
                      >
                        <Star className={cn("w-3 h-3", minRating === opt.value ? "fill-amber-400" : "")} />
                        {opt.label}
                      </button>
                    ))}
                  </div>

                  <div className="hidden sm:block w-px bg-white/[0.06] self-stretch" />

                  {/* Price */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] font-semibold text-white/25 uppercase tracking-widest w-16 shrink-0">
                      Price
                    </span>
                    {PRICE_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => setMaxPrice((v) => v === opt.value ? null : opt.value)}
                        className={cn(
                          "flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium border transition-all",
                          maxPrice === opt.value
                            ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/40 shadow-lg shadow-emerald-500/10"
                            : "bg-white/[0.04] text-white/50 border-white/[0.08] hover:bg-white/[0.07] hover:text-white/80"
                        )}
                      >
                        <DollarSign className="w-3 h-3" />
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Clear + result count */}
                <AnimatePresence>
                  {hasFilters && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="flex items-center justify-between pt-1 border-t border-white/[0.06]"
                    >
                      <p className="text-xs text-white/40">
                        <span className="text-white/70 font-semibold">{filtered.length}</span> trainer{filtered.length !== 1 ? "s" : ""} match
                        {myGymOnly && primaryGym && <> · <span className="text-primary">{primaryGym.gym?.name}</span></>}
                        {activeSpecialties.size > 0 && <> · {[...activeSpecialties].join(", ")}</>}
                        {minRating !== null && <> · <Star className="w-2.5 h-2.5 inline fill-amber-400 text-amber-400 -mt-0.5" /> {minRating}+</>}
                        {maxPrice !== null && <> · under ${maxPrice}/hr</>}
                      </p>
                      <button
                        onClick={clearFilters}
                        className="flex items-center gap-1 text-xs text-white/35 hover:text-white/60 transition-colors"
                      >
                        <X className="w-3 h-3" /> Clear all
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </motion.div>

          {/* Sort bar */}
          {!isLoading && !studentStillLoading && filtered.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="max-w-3xl mx-auto mb-6 flex items-center gap-2 flex-wrap"
            >
              <span className="text-xs text-white/30 flex items-center gap-1.5 shrink-0">
                <ArrowUpDown className="w-3.5 h-3.5" /> Sort:
              </span>
              {SORT_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setSortBy(opt.value)}
                  className={cn(
                    "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all",
                    sortBy === opt.value
                      ? "bg-white/10 text-white border-white/20 shadow-sm"
                      : "bg-transparent text-white/40 border-white/[0.07] hover:bg-white/[0.05] hover:text-white/70"
                  )}
                >
                  {opt.icon}
                  {opt.label}
                </button>
              ))}
            </motion.div>
          )}

          {/* Results */}
          {isLoading || studentStillLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="h-72 bg-white/[0.03] rounded-2xl animate-pulse border border-white/[0.06]" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20">
              <Users className="w-12 h-12 text-white/20 mx-auto mb-4" />
              <p className="text-white/50 text-lg mb-4">No trainers match your current filters.</p>
              <Button variant="ghost" onClick={clearFilters} className="text-primary hover:text-primary hover:bg-primary/10">
                <X className="w-4 h-4 mr-2" /> Clear all filters
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((trainer, i) => (
                <motion.div
                  key={trainer.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04 }}
                >
                  <div className="group relative rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 hover:bg-white/[0.04] transition-all duration-300 hover:-translate-y-1 h-full flex flex-col">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-14 h-14 rounded-xl bg-secondary flex items-center justify-center text-xl font-bold text-white shrink-0 overflow-hidden">
                        {trainer.avatarUrl ? (
                          <img src={trainer.avatarUrl} alt={`${trainer.firstName} ${trainer.lastName} profile photo`} className="w-full h-full object-cover" loading="lazy" />
                        ) : (
                          trainer.firstName[0]
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h2 className="text-xl font-display font-bold text-white">
                          {trainer.firstName} {trainer.lastName}
                        </h2>
                        <div className="flex items-center gap-2 mt-1">
                          {trainer.avgRating != null ? (
                            <span className="flex items-center gap-1 text-sm text-primary">
                              <Star className="w-3.5 h-3.5 fill-primary" />
                              {Number(trainer.avgRating).toFixed(1)}
                            </span>
                          ) : (
                            <span className="text-xs text-white/25">No ratings yet</span>
                          )}
                          <span className="text-sm text-white/40">{trainer.totalSessions} sessions</span>
                        </div>
                      </div>
                    </div>

                    {trainer.bio && (
                      <p className="text-sm text-white/50 line-clamp-2 mb-3">{trainer.bio}</p>
                    )}

                    {trainer.specialties && trainer.specialties.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-4">
                        {trainer.specialties.slice(0, 4).map((s) => (
                          <button
                            key={s}
                            onClick={() => toggleSpecialty(s)}
                            className={cn(
                              "px-2.5 py-0.5 rounded-lg text-xs border transition-all",
                              activeSpecialties.has(s)
                                ? "bg-primary/20 text-primary border-primary/40"
                                : "bg-white/[0.04] text-white/50 border-white/[0.08] hover:bg-white/[0.08] hover:text-white/70"
                            )}
                          >
                            {s}
                          </button>
                        ))}
                        {trainer.specialties.length > 4 && (
                          <span className="px-2.5 py-0.5 rounded-lg text-xs text-white/30 border border-white/[0.06]">
                            +{trainer.specialties.length - 4}
                          </span>
                        )}
                      </div>
                    )}

                    <div className="mt-auto pt-4 border-t border-white/[0.06] flex items-center justify-between">
                      <div className="flex flex-col gap-0.5">
                        {trainer.hourlyRate != null ? (
                          <span className={cn(
                            "text-lg font-mono font-bold",
                            maxPrice !== null && Number(trainer.hourlyRate) < maxPrice ? "text-emerald-400" : "text-primary"
                          )}>
                            ${trainer.hourlyRate}/hr
                          </span>
                        ) : (
                          <span className="text-sm text-white/30">Rate on request</span>
                        )}
                        {trainer.gymName && (
                          <Link href={`/gym/${trainer.gymSlug}`}>
                            <span className="text-xs text-white/40 hover:text-primary transition-colors flex items-center gap-1 cursor-pointer">
                              <MapPin className="w-3 h-3" />
                              {trainer.gymName}
                            </span>
                          </Link>
                        )}
                      </div>
                      {trainer.publicSlug ? (
                        <Link href={`/trainer/${trainer.publicSlug}`}>
                          <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold rounded-xl gap-1">
                            View Profile <ArrowRight className="w-3.5 h-3.5" />
                          </Button>
                        </Link>
                      ) : (
                        <Link href={`/book?trainerId=${trainer.id}&gymId=${trainer.gymId || ""}`}>
                          <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold rounded-xl gap-1">
                            Book <ArrowRight className="w-3.5 h-3.5" />
                          </Button>
                        </Link>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </SmartLayout>
  );
}
